import { consoleCopy, contentCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { AvatarGroup } from '@/ui/AvatarGroup/AvatarGroup'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusDot } from '@/ui/Status/Status'
import { useTopicQuery } from '../../hooks/queries'
import { useAgentLiveStates } from '../../hooks/useAgentLiveState'
import { RefreshFailed } from '../RefreshFailed'
import styles from './TopicAgentHeader.module.css'

interface Props {
  topicId: string
  workspaceId?: string
}

/**
 * Always-visible agent header (§3): per-agent avatar, `primary` chip and
 * state line; below it a summary line and the people avatar group. Role
 * comes from the topic's `primary_agent_id` (server-enforced
 * exactly-one); state from the shared derivation.
 */
export function TopicAgentHeader({ topicId, workspaceId }: Props) {
  const { data: topic, isLoading, isError, refetch } = useTopicQuery(topicId)
  const { stateOf } = useAgentLiveStates(workspaceId)
  const showSkeleton = useSkeletonDelay(isLoading)
  const agents = topic?.agents ?? []
  const primaryId = topic?.primary_agent_id ?? null

  const counts = { running: 0, blocked: 0, paused: 0 }
  for (const a of agents) {
    const s = stateOf(a.agent_id)
    if (s === 'running') counts.running += 1
    else if (s === 'blocked') counts.blocked += 1
    else if (s === 'paused') counts.paused += 1
  }
  const summary = [
    consoleCopy.topic.agents(agents.length),
    counts.running > 0 ? consoleCopy.topic.running(counts.running) : null,
    counts.blocked > 0 ? consoleCopy.topic.blocked(counts.blocked) : null,
    counts.paused > 0 ? consoleCopy.topic.paused(counts.paused) : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const people = topic?.users ?? []

  let body: React.ReactNode
  if (isLoading) {
    body = showSkeleton ? (
      <Card>
        <Skeleton lines={2} ground="surface" />
      </Card>
    ) : null
  } else if (isError && !topic) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {consoleCopy.topic.loadFailed}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {consoleCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  } else {
    body = (
      <Card>
        <Inline gap="space-16">
          {agents.map((a) => {
            const state = stateOf(a.agent_id)
            return (
              <Inline key={a.agent_id} gap="space-8" wrap={false}>
                <Avatar name={a.name} kind="agent" size="md" />
                <div className={styles.agentText}>
                  <Inline gap="space-6" wrap={false}>
                    <Text tone="text">{a.name}</Text>
                    {a.agent_id === primaryId && (
                      <Chip variant="kind" label={consoleCopy.topic.primary} />
                    )}
                  </Inline>
                  <Text variant="caption" tone="text-sub">
                    <StatusDot
                      tone={lifecycleTone(state)}
                      label={consoleCopy.agentState[state]}
                    />
                  </Text>
                </div>
              </Inline>
            )
          })}
          {agents.length === 0 && (
            <EmptyState
              variant="inline"
              description={consoleCopy.topic.noAgents}
            />
          )}
        </Inline>
        <Inline gap="space-8" wrap={false}>
          <Text
            variant="caption"
            tone="text-mute"
            numeric
            className={styles.grow}
          >
            {summary}
          </Text>
          {people.length > 0 && (
            <AvatarGroup
              ground="surface"
              participants={people.map((u) => ({ name: u, kind: 'human' }))}
              label={consoleCopy.topic.people(
                contentCopy.avatarGroup.label(
                  people.slice(0, 4),
                  Math.max(0, people.length - 4),
                ),
              )}
            />
          )}
        </Inline>
      </Card>
    )
  }

  return (
    <div className={styles.strip} aria-busy={isLoading || undefined}>
      {isError && topic && (
        <RefreshFailed
          message={consoleCopy.topic.loadFailed}
          onRetry={() => void refetch()}
        />
      )}
      {body}
    </div>
  )
}
